import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';

import type { BillingPlanOfferDto, CompanyBillingSummaryDto } from '@shared/services/apiService';

import { formatBillingMoney, formatCreditUnits } from '../model/formatBillingUnits';

export const BillingCheckoutReview = ({
  plan,
  summary,
  busy,
  onClose,
  onConfirm,
}: {
  plan: BillingPlanOfferDto | null;
  summary?: CompanyBillingSummaryDto;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) => (
  <Dialog open={!!plan} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
    <DialogTitle>Review your plan</DialogTitle>
    <DialogContent>
      {plan && (
        <Stack spacing={2.5}>
          <Typography variant="h5">{plan.key}</Typography>
          <Typography color="text.secondary">Monthly company subscription</Typography>
          <Divider />
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Typography>Monthly recurring amount</Typography>
            <Typography fontWeight={600}>
              {formatBillingMoney(plan.amountMinor, plan.currency, plan.currencyScale)}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Typography>Credits per paid period</Typography>
            <Typography fontWeight={600}>
              {formatCreditUnits(plan.allowanceUnits, plan.creditScale)}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Typography>Simultaneous calls</Typography>
            <Typography fontWeight={600}>{plan.concurrentLimit}</Typography>
          </Stack>
          {summary?.status === 'trial' && (
            <Alert severity="warning">
              Unused trial credits expire when the paid plan activates. The paid package does not
              stack with or extend the trial.
            </Alert>
          )}
          <Typography color="text.secondary">
            Credits expire at each service-period boundary. Your company shares this allowance and
            call capacity. The plan does not change user permissions.
          </Typography>
          <Alert severity="info">
            Secure payment takes place in Stripe Checkout, where you review taxes and the final
            amount before paying. Access changes only after server-confirmed activation.
          </Alert>
        </Stack>
      )}
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 3, flexWrap: 'wrap', gap: 1 }}>
      <Button disabled={busy} onClick={onClose}>
        Change plan
      </Button>
      <Button variant="contained" disabled={busy || !plan} onClick={onConfirm}>
        Continue to Stripe Checkout
      </Button>
    </DialogActions>
  </Dialog>
);
